import {
  BACKGROUND_FAR_STAR_COUNT,
  BACKGROUND_FAR_STAR_SPEED,
  BACKGROUND_NEAR_STAR_COUNT,
  BACKGROUND_NEAR_STAR_SPEED,
  BACKGROUND_STARS_COLOR,
  STARS_ACCELERATION_MAX,
  STARS_ACCELERATION_MAX_TAPS,
  STARS_ACCELERATION_MIN,
} from '../../../ClickerGame.constants';
import {ClickerGCSEngine} from '../../../ClickerGame.types';
import {backgroundFarStarConfig, backgroundNearStarConfig} from './BackgroundStars.config';
import {DI_TOKENS} from '@/di/di.tokens';
import debounce from 'lodash.debounce';
import {Particle, ParticleContainer, Rectangle} from 'pixi.js';

import {isRectIntersectsRect} from 'aabb-collisions-detector';
import {injected} from 'brandi';

import {BaseComponent} from '@specials/gcs-engine';
import {ParticleEmitter, SpawnShapeBehavior, SpawnShapeType, noUseParticle} from '@specials/particle-emitter';

import {CallbackCollector} from '@/utils/CallbackCollector';
import {NumberUtils} from '@/utils/NumberUtils';
import {ParticleAdapter} from '@/utils/ParticleAdapter';
import {ParticleContainerAdapter} from '@/utils/ParticleContainerAdapter';

import {ClickerGameAssetsLoader} from '../../../ClickerGameAssetsLoader';
import {ClickerGameEventNames, ClickerGameEvents} from '../../../ClickerGameEvents';
import {BackgroundGalaxyViewComponent} from './BackgroundGalaxyViewComponent';

export class BackgroundGalaxyStarsComponent extends BaseComponent {
  private viewComponent: BackgroundGalaxyViewComponent;
  private dimensions: Rectangle;
  private acceleration: number;
  private tapsCounter: number;
  private nearStarsEmitter: ParticleEmitter;
  private farStarsEmitter: ParticleEmitter;

  private particleContainer: ParticleContainer;

  constructor(
    private readonly gcsEngine: ClickerGCSEngine,
    private readonly assetsLoader: ClickerGameAssetsLoader,
    private readonly gameEvents: ClickerGameEvents,
    private readonly callbackCollector: CallbackCollector,
  ) {
    super();
  }

  public start(): void {
    const viewport = this.gcsEngine.renderer.renderer.getFrameDimensions();
    this.dimensions = new Rectangle(0, -100, viewport.width, viewport.height);
    this.viewComponent = this.gameObject.getComponent(BackgroundGalaxyViewComponent)!;

    this.acceleration = STARS_ACCELERATION_MIN;
    this.tapsCounter = 0;

    this.particleContainer = new ParticleContainer({
      dynamicProperties: {
        position: true,
        scale: true,
        rotation: false,
        tint: false,
      },
    });

    this.viewComponent.view.addChild(this.particleContainer);

    const initSpawnShape: SpawnShapeBehavior = {
      shape: {
        type: SpawnShapeType.Rectangle,
        x: 0,
        y: 0,
        width: this.dimensions.width,
        height: this.dimensions.height,
      },
    };

    this.nearStarsEmitter = new ParticleEmitter<ParticleAdapter>(
      new ParticleContainerAdapter(this.particleContainer),
      this.createStar,
      backgroundNearStarConfig(initSpawnShape),
    );
    this.farStarsEmitter = new ParticleEmitter<ParticleAdapter>(
      new ParticleContainerAdapter(this.particleContainer),
      this.createStar,
      backgroundFarStarConfig(initSpawnShape),
    );

    this.nearStarsEmitter.emitOnce(BACKGROUND_NEAR_STAR_COUNT);
    this.farStarsEmitter.emitOnce(BACKGROUND_FAR_STAR_COUNT);

    const spawnShape: SpawnShapeBehavior = {
      shape: {
        type: SpawnShapeType.Rectangle,
        x: 0,
        y: -100,
        width: this.dimensions.width,
        height: 100,
      },
    };

    this.nearStarsEmitter.config.spawnShape = spawnShape;
    this.farStarsEmitter.config.spawnShape = spawnShape;

    this.callbackCollector.add(
      this.gameEvents.subscribeOnEvent(ClickerGameEventNames.Tap, () => {
        this.addTap(1);

        this.slowDownAcceleration();
      }),
    );
  }

  public destroy(): void {
    this.callbackCollector.execute();
    this.slowDownAcceleration.cancel();
    this.gcsEngine.time.clearInterval(this.handleSlowDownInterval);
  }

  public update(delta: number): void {
    this.updateEmitter(this.nearStarsEmitter, BACKGROUND_NEAR_STAR_SPEED, delta);
    this.updateEmitter(this.farStarsEmitter, BACKGROUND_FAR_STAR_SPEED, delta);
  }

  private updateEmitter(emitter: ParticleEmitter, speed: number, delta: number): void {
    emitter.updateContainer(delta, this.gcsEngine.time.getTimeMs());

    emitter.getParticles().forEach((star) => {
      if (
        !isRectIntersectsRect(this.dimensions, {
          x: star.view.x,
          y: star.view.y,
          width: star.view.width,
          height: star.view.height,
        })
      ) {
        noUseParticle(star);
        emitter.emitOnce(1);
      } else {
        star.speed = (speed + this.acceleration) * delta;
      }
    });
  }

  private slowDownAcceleration = debounce((): void => {
    this.gcsEngine.time.clearInterval(this.handleSlowDownInterval);
    this.gcsEngine.time.setInterval(this.handleSlowDownInterval, 150);
  }, 700);

  private handleSlowDownInterval = (): void => {
    this.addTap(-1);
    if (this.tapsCounter === 0) {
      this.gcsEngine.time.clearInterval(this.handleSlowDownInterval);
    }
  };

  private updateAcceleration(): void {
    this.acceleration = NumberUtils.lerp(
      STARS_ACCELERATION_MIN,
      STARS_ACCELERATION_MAX,
      this.tapsCounter / STARS_ACCELERATION_MAX_TAPS,
    );
  }

  private addTap(count: number): void {
    this.tapsCounter = NumberUtils.clamp(0, STARS_ACCELERATION_MAX_TAPS, this.tapsCounter + count);
    this.updateAcceleration();
  }

  private createStar = (): ParticleAdapter => {
    const star = new ParticleAdapter(
      new Particle({texture: this.assetsLoader.bundle.bloomParticle, anchorX: 0.5, anchorY: 0.5}),
    );
    // star.anchor.set(0.5);
    star.tint = BACKGROUND_STARS_COLOR;

    return star;
  };
}

injected(
  BackgroundGalaxyStarsComponent,
  DI_TOKENS.gcsEngine,
  DI_TOKENS.clickerGameAssetsLoader,
  DI_TOKENS.clickerGameEvents,
  DI_TOKENS.callbackCollector,
);
